import { getDisplayTimeZone } from "./timezone";

export type FormatTimestampOptions = {
  /** Override the locale used by Intl.DateTimeFormat. Defaults to the browser locale. */
  locale?: string;
  /** Time zone passed to Intl.DateTimeFormat. Defaults to UTC to mirror backend normalization. */
  timeZone?: string;
  /** Fallback string returned when the input value is null/undefined/empty. */
  fallback?: string;
  /** Formatter override for Intl.DateTimeFormat; merged with defaults. */
  formatOptions?: Intl.DateTimeFormatOptions;
};

const DEFAULT_FALLBACK = "N/A";
const DEFAULT_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "medium",
};

const FIELD_LEVEL_OPTION_KEYS: Array<keyof Intl.DateTimeFormatOptions> = [
  "weekday",
  "era",
  "year",
  "month",
  "day",
  "dayPeriod",
  "hour",
  "minute",
  "second",
  "fractionalSecondDigits",
  "timeZoneName",
];

const hasFieldLevelOptions = (options?: Intl.DateTimeFormatOptions) =>
  Boolean(options && Object.keys(options).some((key) => FIELD_LEVEL_OPTION_KEYS.includes(key as keyof Intl.DateTimeFormatOptions)));

export function formatTimestamp(
  value: string | number | Date | null | undefined,
  options: FormatTimestampOptions = {},
): string {
  const { locale, timeZone = getDisplayTimeZone(), fallback = DEFAULT_FALLBACK, formatOptions } = options;

  if (value == null || value === "") return fallback;

  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "Invalid";

    const shouldSkipDefaults = hasFieldLevelOptions(formatOptions);
    const formatterOptions: Intl.DateTimeFormatOptions = {
      ...(shouldSkipDefaults ? {} : DEFAULT_FORMAT),
      ...formatOptions,
      timeZone,
    };

    const formatter = new Intl.DateTimeFormat(locale, formatterOptions);

    return formatter.format(date);
  } catch {
    return "Invalid";
  }
}
